import React, { useEffect, useRef } from 'react';

import vtkGenericRenderWindow from '@kitware/vtk.js/Rendering/Misc/GenericRenderWindow';

import vtkVolume from '@kitware/vtk.js/Rendering/Core/Volume';
import vtkVolumeMapper from '@kitware/vtk.js/Rendering/Core/VolumeMapper';
import { vtkWidgetManager } from '@kitware/vtk.js/Widgets/Core/WidgetManager';
import vtkOrientationMarkerWidget from '@kitware/vtk.js/Interaction/Widgets/OrientationMarkerWidget';

import vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import vtkDataArray from '@kitware/vtk.js/Common/Core/DataArray';
import { mat4, vec3, mat3, vec4, quat } from 'gl-matrix';
import vtkPolyData from '@kitware/vtk.js/Common/DataModel/PolyData';
import vtkPoints from '@kitware/vtk.js/Common/Core/Points';
import vtkCellArray from '@kitware/vtk.js/Common/Core/CellArray';

import vtkRenderWindow from '@kitware/vtk.js/Rendering/Core/RenderWindow';
import vtkImageMapper from '@kitware/vtk.js/Rendering/Core/ImageMapper';
import vtkColorTransferFunction from '@kitware/vtk.js/Rendering/Core/ColorTransferFunction';
import vtkPiecewiseFunction from '@kitware/vtk.js/Common/DataModel/PiecewiseFunction';
import vtkImageCPRMapper from '@kitware/vtk.js/Rendering/Core/ImageCPRMapper';
import vtkImageSlice from '@kitware/vtk.js/Rendering/Core/ImageSlice';
import vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer';
import vtkImageReslice from '@kitware/vtk.js/Imaging/Core/ImageReslice';
// @ts-ignore
import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import vtkMapper from '@kitware/vtk.js/Rendering/Core/Mapper';

import { traceOutlinesWithCenterOption } from './FunctionUtilites';
import {
  Actors,
  BufferPoolItem,
  CPRContext,
  CrossSectionContext,
} from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import { vtkRange } from '@kitware/vtk.js/interfaces';

import vtkCardinalSpline1D from '@kitware/vtk.js/Common/DataModel/CardinalSpline1D';
import { Vector3 } from '@kitware/vtk.js/types';
import {
  makeCoronaryColorPoints,
  makeOpacityPoints,
  rgbArrayToHex,
  rgbToNormalizedARGB,
  updateAVCColor,
  updateAVCOpacity,
} from 'config/colorUtils';
import { configStore } from 'config/configStore';
import { AnalysisType, VOI } from 'config/analysisConfig';
import { InterpolationMode } from '@kitware/vtk.js/Imaging/Core/AbstractImageInterpolator/Constants';

const lastValidCPRState = new WeakMap<
  vtkImageCPRMapper,
  { direction: mat3; height: number }
>();

export interface PickPoints {
  position: vec3;
  normal?: vec3;
  viewNormal?: vec3;
  index?: [number, number, number];
}

export interface ContextRef {
  genericRenderWindow: vtkGenericRenderWindow;
  renderWindow: vtkRenderWindow;
  renderer: vtkRenderer;
  widgetManager?: vtkWidgetManager;
  actor?: vtkImageSlice | vtkVolume;
  paintActor?: vtkImageSlice;
  mapper?: vtkImageMapper | vtkVolumeMapper | vtkImageCPRMapper;
  paintMapper?: vtkImageMapper | vtkImageCPRMapper;
  orientationWidget?: vtkOrientationMarkerWidget;
  reslice?: vtkImageReslice;
  paintReslice?: vtkImageReslice;
  pickPoints?: Record<string, PickPoints>;
  contourActor?: vtkActor | null;
}

// vtk render container 만드는 함수
// vtk 올리기 위한 기본 판이라 생각하면 됨
export function useGenericRenderWindow({
  vtkContainerRef,
}: {
  vtkContainerRef: React.RefObject<HTMLDivElement | null>;
}) {
  const context = useRef<ContextRef | null>(null);

  useEffect(() => {
    if (!context.current) {
      const genericRenderWindow = vtkGenericRenderWindow.newInstance();
      genericRenderWindow.setContainer(vtkContainerRef.current!);
      const renderer = genericRenderWindow.getRenderer();
      const renderWindow = genericRenderWindow.getRenderWindow();
      genericRenderWindow.setBackground([0.05, 0.05, 0.05]);

      renderer.resetCamera();
      renderWindow.render();
      genericRenderWindow.resize();

      const canvas = vtkContainerRef.current?.querySelector('canvas');
      if (canvas) {
        canvas.style.width = '100%';
        canvas.style.height = '100%';
      }

      context.current = {
        genericRenderWindow,
        renderWindow,
        renderer,
      };
    }

    return () => {
      if (context.current) {
        const { genericRenderWindow, renderWindow, renderer, widgetManager } =
          context.current;
        widgetManager?.removeWidgets();
        renderer.removeAllViewProps();
        renderer.removeAllActors();
        renderWindow?.delete();
        genericRenderWindow?.delete();
        context.current = null;
      }
    };
  }, [vtkContainerRef]);

  return context;
}

export function modifyVtkImageDataProps({
  imageData,
  dimensions,
  spacing,
  origin,
  direction,
}: {
  imageData: vtkImageData;
  dimensions: number[];
  spacing: number[];
  origin: Vector3;
  direction: mat3;
}) {
  imageData.setDimensions(dimensions);
  imageData.setSpacing(spacing);
  imageData.setOrigin(origin);
  imageData.setDirection(direction);
  imageData.setExtent([
    0,
    dimensions[0] - 1,
    0,
    dimensions[1] - 1,
    0,
    dimensions[2] - 1,
  ]);
  imageData.modified();
}
export function hu_raw_to_vtkimage({
  scalarArray,
  dimensions = [0, 0, 0],
  spacing = [1, 1, 1],
  origin = [0, 0, 0],
  direction = mat3.create(),
}: {
  scalarArray: Int16Array | null;
  dimensions?: number[];
  spacing?: number[];
  origin?: Vector3;
  direction?: mat3;
}) {
  if (!scalarArray) return null;
  // if (!dimensions) dimensions = [0, 0, 0];
  // if (!spacing) spacing = [1, 1, 1];
  // if (!origin) origin = [0, 0, 0];
  // if (!direction) direction = mat3.create();

  const imageData = vtkImageData.newInstance();
  // imageData.setDimensions(dimensions);
  // imageData.setSpacing(spacing);
  // imageData.setOrigin(origin);
  // imageData.setDirection(direction);
  // imageData.setExtent([
  //   0,
  //   dimensions[0] - 1,
  //   0,
  //   dimensions[1] - 1,
  //   0,
  //   dimensions[2] - 1,
  // ]);

  const scalars = vtkDataArray.newInstance({
    name: 'HU',
    values: scalarArray,
    numberOfComponents: 1,
  });

  // ✅ 실제 HU 범위 계산해서 range 설정
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (let i = 0; i < scalarArray.length; i++) {
    const v = scalarArray[i];
    if (v < min) min = v;
    if (v > max) max = v;
  }
  scalars.setRange([min, max] as unknown as vtkRange, 0);

  modifyVtkImageDataProps({
    imageData,
    dimensions,
    spacing,
    origin,
    direction,
  });
  imageData.getPointData().setScalars(scalars);
  imageData.modified();

  return imageData;
}

// raw data to vtk image data function
// image data나 label data를 vtk image로 변환하는 함수
export function raw_to_vtkimage({
  scalarArray,
  dimensions,
  spacing,
  origin,
  direction,
}: {
  scalarArray: Uint8Array | null;
  dimensions?: number[];
  spacing?: number[];
  origin?: Vector3;
  direction?: mat3;
}) {
  if (!scalarArray) return null;
  if (!dimensions) dimensions = [0, 0, 0];
  if (!spacing) spacing = [1, 1, 1];
  if (!origin) origin = [0, 0, 0];
  if (!direction) direction = mat3.create();
  const imageData = vtkImageData.newInstance();
  imageData.setDimensions(dimensions);
  imageData.setSpacing(spacing);
  imageData.setOrigin(origin);
  imageData.setDirection(direction);
  imageData.setExtent([
    0,
    dimensions[0] - 1,
    0,
    dimensions[1] - 1,
    0,
    dimensions[2] - 1,
  ]);

  const scalars = vtkDataArray.newInstance({
    name: '',
    values: new Uint8Array(scalarArray),
    numberOfComponents: 1,
  });
  if (scalarArray instanceof Uint8Array) {
    const range: [number, number] = [0, 255];
    scalars.setRange(range as unknown as vtkRange, 0);
  }

  imageData.getPointData().setScalars(scalars);
  imageData.modified();

  return imageData;
}

// center line data to vtk poly data function(center line vector calculate)
// center line 데이터를 vtkpolydata로 변환하는 함수
// center line 데이터는 x, y, z순으로 되어있는 1차원 row data 넣어야함
// cpr 문제 해결하고 원본코드
// export function createCenterline(
//   cendata: Float32Array | null,
//   spacing: [number, number, number] = [1, 1, 1],
//   origin: [number, number, number] = [0, 0, 0],
//   direction: mat3 = mat3.create(),
// ) {
//   if (!cendata) return null;

//   const numPoints = cendata.length / 3;

//   const points = vtkPoints.newInstance();
//   const lines = vtkCellArray.newInstance({
//     values: new Uint16Array(1 + numPoints),
//   });

//   const pointArray: number[] = [];
//   const orientationQuatArray: number[] = [];

//   const getScaled = (i: number) => {
//     const idx = vec3.fromValues(
//       cendata[i * 3] * spacing[0],
//       cendata[i * 3 + 1] * spacing[1],
//       cendata[i * 3 + 2] * spacing[2],
//     );
//     const world = vec3.create();
//     vec3.transformMat3(world, idx, direction);
//     world[0] += origin[0];
//     world[1] += origin[1];
//     world[2] += origin[2];
//     return world as vec3;
//   };

//   const p0 = getScaled(0);
//   const p1 = getScaled(numPoints - 1);
//   const globalAxis = vec3.create();
//   vec3.subtract(globalAxis, p1, p0);
//   const axisLength = vec3.length(globalAxis);
//   vec3.normalize(globalAxis, globalAxis);

//   let prevQ: quat | null = null;

//   for (let y = 0; y < numPoints; y++) {
//     const cur = getScaled(y);
//     pointArray.push(cur[0], cur[1], cur[2]);

//     let dy = Math.ceil(numPoints / axisLength) * 2;
//     dy = Math.min(dy, y, numPoints - y - 1);

//     let prev = getScaled(y - dy);
//     let next = getScaled(y + dy);

//     if (y === 0 && dy === 0) next = getScaled(1);
//     if (y === numPoints - 1 && dy === 0) prev = getScaled(y - 1);

//     const axis_local = vec3.create();
//     vec3.subtract(axis_local, next, prev);
//     vec3.normalize(axis_local, axis_local);

//     if (axis_local[0] === 0 && axis_local[1] === 0 && axis_local[2] === 0) {
//       const before = getScaled(Math.max(0, y - 1));
//       vec3.subtract(axis_local, next, before);
//       vec3.scale(axis_local, axis_local, 0.5);
//       vec3.normalize(axis_local, axis_local);
//     }

//     const ref = vec3.fromValues(0, 0, 1);
//     const bitangent = vec3.create();
//     vec3.cross(bitangent, ref, axis_local);

//     if (vec3.sqrLen(bitangent) < 1e-10) {
//       vec3.cross(bitangent, vec3.fromValues(1, 0, 0), axis_local);
//     }
//     vec3.normalize(bitangent, bitangent);

//     const normal = vec3.create();
//     vec3.cross(normal, axis_local, bitangent);
//     vec3.normalize(normal, normal);

//     const R = mat3.create();
//     R[0] = bitangent[0]; R[1] = bitangent[1]; R[2] = bitangent[2];
//     R[3] = normal[0];    R[4] = normal[1];    R[5] = normal[2];
//     R[6] = axis_local[0]; R[7] = axis_local[1]; R[8] = axis_local[2];

//     const q = quat.create();
//     quat.fromMat3(q, R);
//     quat.normalize(q, q);

//     if (prevQ && quat.dot(prevQ, q) < 0) {
//       q[0] = -q[0]; q[1] = -q[1]; q[2] = -q[2]; q[3] = -q[3];
//     }
//     prevQ = quat.clone(q);

//     orientationQuatArray.push(q[0], q[1], q[2], q[3]);
//   }

//   points.setData(Float32Array.from(pointArray), 3);
//   lines.setData(Uint16Array.from([numPoints, ...[...Array(numPoints).keys()]]));

//   const polyData = vtkPolyData.newInstance();
//   polyData.setPoints(points);
//   polyData.setLines(lines);

//   const orientationData = vtkDataArray.newInstance({
//     name: 'Orientation',
//     numberOfComponents: 4,
//     values: Float32Array.from(orientationQuatArray),
//   });

//   polyData.getPointData().addArray(orientationData);
//   polyData.getPointData().setTensors(orientationData);

//   polyData.modified();
//   return polyData;
// }

export function createCenterline(
  cendata: Float32Array | null,
  spacing: [number, number, number] = [1, 1, 1],
  origin: [number, number, number] = [0, 0, 0],
  direction: mat3 = mat3.create(),
) {
  if (!cendata) return null;

  const numPoints = cendata.length / 3;
  if (numPoints < 2) return null;

  const points = vtkPoints.newInstance();
  const lines = vtkCellArray.newInstance({
    values: new Uint16Array(1 + numPoints),
  });

  const pointArray: number[] = [];
  const orientationMat4Array: number[] = [];
  const orientationQuatArray: number[] = [];
  const EPS2 = 1e-12;
  const COS_FLIP_T = -0.5;
  const SMOOTH_W = 0.2;
  const MAX_ANGLE_DEG = 12;
  const COS_MAX = Math.cos((MAX_ANGLE_DEG * Math.PI) / 180);
  const refX = vec3.fromValues(1, 0, 0);
  const refY = vec3.fromValues(0, 1, 0);
  const refZ = vec3.fromValues(0, 0, 1);

  const safeNormalize = (v: vec3, fallback?: vec3) => {
    const l2 = vec3.sqrLen(v);
    if (!Number.isFinite(l2) || l2 < EPS2) {
      if (fallback) vec3.copy(v, fallback);
      else vec3.set(v, 1, 0, 0);
    }
    vec3.normalize(v, v);
  };

  const pickInitRef = (t: vec3) => {
    const ax = Math.abs(vec3.dot(t, refX));
    const ay = Math.abs(vec3.dot(t, refY));
    const az = Math.abs(vec3.dot(t, refZ));
    if (ax <= ay && ax <= az) return refX;
    if (ay <= az) return refY;
    return refZ;
  };

  const getScaled = (i: number) => {
    const idx = vec3.fromValues(
      cendata[i * 3] * spacing[0],
      cendata[i * 3 + 1] * spacing[1],
      cendata[i * 3 + 2] * spacing[2],
    );
    const world = vec3.create();
    vec3.transformMat3(world, idx, direction);
    world[0] += origin[0];
    world[1] += origin[1];
    world[2] += origin[2];
    return world as vec3;
  };

  // dy 계산용 global axis
  const p0 = getScaled(0);
  const p1 = getScaled(numPoints - 1);
  const globalAxis = vec3.create();
  vec3.subtract(globalAxis, p1, p0);
  let axisLength = vec3.length(globalAxis);
  if (!Number.isFinite(axisLength) || axisLength < 1e-6) axisLength = 1.0;
  safeNormalize(globalAxis, refZ);

  let prevT: vec3 | null = null;
  let prevB: vec3 | null = null;
  let prevQ: quat | null = null;

  for (let y = 0; y < numPoints; y++) {
    const cur = getScaled(y);
    pointArray.push(cur[0], cur[1], cur[2]);

    let dy = Math.ceil(numPoints / axisLength) * 2;
    dy = Math.min(dy, y, numPoints - y - 1);

    let prevP = getScaled(y - dy);
    let nextP = getScaled(y + dy);
    if (y === 0 && dy === 0) nextP = getScaled(1);
    if (y === numPoints - 1 && dy === 0) prevP = getScaled(y - 1);

    const T = vec3.create();
    vec3.subtract(T, nextP, prevP);

    if (vec3.sqrLen(T) < EPS2) {
      if (y + 1 < numPoints) {
        const pN = getScaled(y + 1);
        vec3.subtract(T, pN, cur);
      } else if (y - 1 >= 0) {
        const pB0 = getScaled(y - 1);
        vec3.subtract(T, cur, pB0);
      }
    }
    safeNormalize(T, prevT ?? globalAxis);

    if (prevT && vec3.dot(prevT, T) < COS_FLIP_T) {
      vec3.scale(T, T, -1);
    }

    if (prevT) {
      const d = Math.max(-1, Math.min(1, vec3.dot(prevT, T)));
      if (d < COS_MAX) {
        const blended = vec3.create();
        vec3.lerp(blended, prevT, T, 1 - SMOOTH_W);
        safeNormalize(blended, T);
        vec3.copy(T, blended);
      }
    }

    let B = vec3.create();

    if (prevB) {
      const proj = vec3.create();
      vec3.scale(proj, T, vec3.dot(prevB, T));
      vec3.subtract(B, prevB, proj);
    } else {
      const initRef = pickInitRef(T);
      vec3.cross(B, initRef, T);
    }

    if (vec3.sqrLen(B) < EPS2) {
      const initRef = pickInitRef(T);
      vec3.cross(B, initRef, T);
    }
    safeNormalize(B);

    if (prevB && vec3.dot(prevB, B) < 0) {
      vec3.scale(B, B, -1);
    }

    const N = vec3.create();
    vec3.cross(N, T, B);
    safeNormalize(N);

    vec3.cross(B, N, T);
    safeNormalize(B);

    prevT = vec3.clone(T);
    prevB = vec3.clone(B);

    const R = mat3.create();
    R[0] = B[0];
    R[1] = B[1];
    R[2] = B[2];
    R[3] = N[0];
    R[4] = N[1];
    R[5] = N[2];
    R[6] = T[0];
    R[7] = T[1];
    R[8] = T[2];

    const q = quat.create();
    quat.fromMat3(q, R);
    quat.normalize(q, q);

    if (prevQ && quat.dot(prevQ, q) < 0) {
      q[0] = -q[0];
      q[1] = -q[1];
      q[2] = -q[2];
      q[3] = -q[3];
    }
    prevQ = quat.clone(q);

    orientationQuatArray.push(q[0], q[1], q[2], q[3]);

    const M = mat4.create();
    mat4.fromQuat(M, q);
    orientationMat4Array.push(...M);
  }

  points.setData(Float32Array.from(pointArray), 3);
  lines.setData(Uint16Array.from([numPoints, ...[...Array(numPoints).keys()]]));

  const polyData = vtkPolyData.newInstance();
  polyData.setPoints(points);
  polyData.setLines(lines);

  const tensorsMat4 = vtkDataArray.newInstance({
    name: 'Orientation',
    numberOfComponents: 16,
    values: Float32Array.from(orientationMat4Array),
  });
  polyData.getPointData().setTensors(tensorsMat4);
  polyData.getPointData().addArray(tensorsMat4);

  const quatArr = vtkDataArray.newInstance({
    name: 'OrientationQuat',
    numberOfComponents: 4,
    values: Float32Array.from(orientationQuatArray),
  });
  polyData.getPointData().addArray(quatArr);

  polyData.modified();
  return polyData;
}

// get Actor painted color label
// label data를 표시하기 위한 vtk actor 만드는 함수
function getColoredPaintActor(
  paintMapper: vtkImageMapper | vtkImageCPRMapper,
  colorPoints: Array<[number, number, number, number]>,
  opacityPoints: Array<[number, number]>,
) {
  const paintActor = vtkImageSlice.newInstance();
  if (colorPoints && opacityPoints) {
    // @ts-ignore
    paintActor.setMapper(paintMapper);
    paintActor.getProperty().setInterpolationTypeToNearest();

    const lut = vtkColorTransferFunction.newInstance();
    colorPoints.forEach(([x, r, g, b]) => {
      lut.addRGBPoint(x, r, g, b);
    });
    lut.updateRange();

    const pwf = vtkPiecewiseFunction.newInstance();
    opacityPoints.forEach(([x, opacity]) => {
      pwf.addPoint(x, opacity);
    });

    paintActor.getProperty().setPiecewiseFunction(0, pwf);
    paintActor.getProperty().setRGBTransferFunction(0, lut);
  }
  return paintActor;
}

// get cross section axes vector
// cross section 단면 계산을 위한 axes 함수
export function CalculateResliceAxes(
  bufferpool: BufferPoolItem,
  branchId: number,
  index: number,
  analysistype?: AnalysisType,
  centerlineOverride?: vtkPolyData,
  imageData?: vtkImageData,
) {
  const centerData =
    centerlineOverride ??
    (bufferpool.centerData.data as Record<number, vtkPolyData>)[branchId];
  const tensors = centerData.getPointData().getTensors();
  const origin = centerData.getPoints().getPoint(index);
  const orientationValues = tensors.getTuple(index);
  const newResliceAxes = mat4.clone(orientationValues as mat4);
  if (analysistype === 'tavi') {
    const parsed = (bufferpool as Record<string, any>)['tavi.parsed']?.data as
      | Record<string, unknown>
      | undefined;
    const readArray = (name: 'centerTangent' | 'center_dx' | 'center_dy') => {
      const raw =
        (parsed?.[name] as unknown) ??
        (bufferpool as Record<string, any>)[`tavi.${name}`]?.data;
      if (!raw) return null;
      if (raw instanceof Float64Array) return raw;
      if (raw instanceof ArrayBuffer) return new Float64Array(raw);
      if (ArrayBuffer.isView(raw)) {
        const view = raw as ArrayBufferView;
        return new Float64Array(
          view.buffer,
          view.byteOffset,
          Math.floor(view.byteLength / Float64Array.BYTES_PER_ELEMENT),
        );
      }
      return null;
    };
    const toWorldVec = (arr: Float64Array | null): vec3 | null => {
      if (!arr) return null;
      const p = index * 3;
      if (p + 2 >= arr.length) return null;
      const v = vec3.fromValues(arr[p + 0], arr[p + 1], arr[p + 2]);
      if (imageData) {
        const spacing = imageData.getSpacing() as [number, number, number];
        const direction = imageData.getDirection() as mat3;
        const scaled = vec3.fromValues(
          v[0] * spacing[0],
          v[1] * spacing[1],
          v[2] * spacing[2],
        );
        vec3.transformMat3(v, scaled, direction);
      }
      if (vec3.sqrLen(v) < 1e-12) return null;
      vec3.normalize(v, v);
      return v;
    };

    const zAxis = toWorldVec(readArray('centerTangent'));
    const xHint = toWorldVec(readArray('center_dx'));
    const yHint = toWorldVec(readArray('center_dy'));

    if (zAxis) {
      let xAxis: vec3 | null = null;
      if (xHint) {
        const proj = vec3.scale(vec3.create(), zAxis, vec3.dot(xHint, zAxis));
        xAxis = vec3.subtract(vec3.create(), xHint, proj);
        if (vec3.sqrLen(xAxis) < 1e-12) xAxis = null;
      }
      if (!xAxis && yHint) {
        xAxis = vec3.cross(vec3.create(), yHint, zAxis);
        if (vec3.sqrLen(xAxis) < 1e-12) xAxis = null;
      }
      if (!xAxis) {
        const ref = Math.abs(vec3.dot(vec3.fromValues(1, 0, 0), zAxis)) < 0.98
          ? vec3.fromValues(1, 0, 0)
          : vec3.fromValues(0, 1, 0);
        xAxis = vec3.cross(vec3.create(), ref, zAxis);
      }
      vec3.normalize(xAxis, xAxis);

      let yAxis = vec3.cross(vec3.create(), zAxis, xAxis);
      vec3.normalize(yAxis, yAxis);
      if (yHint && vec3.dot(yAxis, yHint) < 0) {
        vec3.scale(xAxis, xAxis, -1);
        vec3.scale(yAxis, yAxis, -1);
      }
      xAxis = vec3.cross(vec3.create(), yAxis, zAxis);
      vec3.normalize(xAxis, xAxis);
      yAxis = vec3.cross(vec3.create(), zAxis, xAxis);
      vec3.normalize(yAxis, yAxis);

      newResliceAxes[0] = xAxis[0];
      newResliceAxes[1] = xAxis[1];
      newResliceAxes[2] = xAxis[2];
      newResliceAxes[4] = yAxis[0];
      newResliceAxes[5] = yAxis[1];
      newResliceAxes[6] = yAxis[2];
      newResliceAxes[8] = zAxis[0];
      newResliceAxes[9] = zAxis[1];
      newResliceAxes[10] = zAxis[2];
    }
  }
  if (analysistype !== 'aorticValveCalcium' && analysistype !== 'tavi') {
    const fxData = (bufferpool.fxData.data as Record<number, Vector3[]>)[
      branchId
    ];
    const fyData = (bufferpool.fyData.data as Record<number, Vector3[]>)[
      branchId
    ];
    const expand = configStore.get().coronary_front_expand;
    if (index - expand >= 0 && index - expand < fxData.length) {
      newResliceAxes[0] = fxData[index - expand][0];
      newResliceAxes[1] = fxData[index - expand][1];
      newResliceAxes[2] = fxData[index - expand][2];
      newResliceAxes[4] = fyData[index - expand][0];
      newResliceAxes[5] = fyData[index - expand][1];
      newResliceAxes[6] = fyData[index - expand][2];
    }
  }
  newResliceAxes[12] = origin[0];
  newResliceAxes[13] = origin[1];
  newResliceAxes[14] = origin[2];
  return newResliceAxes;
}

// vtk image camera move function
// 이미지 판 이동 하는 함수
export function MoveImageAndCamera(
  renderWindow: vtkRenderWindow,
  renderer: vtkRenderer,
  moveX: number,
  moveY: number,
) {
  const camera = renderer.getActiveCamera();
  // renderWindow.getInteractor().setInteractorStyle(null);

  const viewUp = camera.getViewUp();
  const direction = camera.getDirectionOfProjection();
  const right = vec3.create();
  vec3.cross(right, direction, viewUp);
  vec3.normalize(right, right);

  const up = vec3.create();
  vec3.cross(up, right, direction);
  vec3.normalize(up, up);

  const translation = vec3.create();
  vec3.scaleAndAdd(translation, translation, right, -moveX);
  vec3.scaleAndAdd(translation, translation, up, moveY);

  const position = vec3.fromValues(...camera.getPosition());
  const focalPoint = vec3.fromValues(...camera.getFocalPoint());

  vec3.add(position, position, translation);
  vec3.add(focalPoint, focalPoint, translation);

  camera.setPosition(...position);
  camera.setFocalPoint(...focalPoint);

  renderer.resetCameraClippingRange();
  renderWindow.render();
}

export type OutlineSmoothingOptions = {
  enabled?: boolean;
  resampleStepPx?: number;
  smoothRadius?: number;
  smoothIterations?: number;
  simplifyTolerancePx?: number;
  minPoints?: number;
  maxPoints?: number;
  splineSamplesPerSegment?: number;
};

export const DEFAULT_CROSS_SECTION_OUTLINE_SMOOTHING: Required<OutlineSmoothingOptions> = {
  enabled: true,
  resampleStepPx: 1.0,
  smoothRadius: 10,
  smoothIterations: 10,
  simplifyTolerancePx: 10,
  minPoints: 20,
  maxPoints: 50,
  splineSamplesPerSegment: 3,
};

const normalizeOutlineOptions = (
  options?: OutlineSmoothingOptions | false,
): Required<OutlineSmoothingOptions> => {
  if (options === false) {
    return {
      ...DEFAULT_CROSS_SECTION_OUTLINE_SMOOTHING,
      enabled: false,
    };
  }
  return {
    ...DEFAULT_CROSS_SECTION_OUTLINE_SMOOTHING,
    ...(options ?? {}),
  };
};

const outlineDistance = (a: [number, number], b: [number, number]) =>
  Math.hypot(a[0] - b[0], a[1] - b[1]);

const isSamePoint2D = (a: [number, number], b: [number, number]) =>
  Math.abs(a[0] - b[0]) < 1e-6 && Math.abs(a[1] - b[1]) < 1e-6;

function stripClosingPoint(outline: [number, number][]) {
  if (
    outline.length > 1 &&
    isSamePoint2D(outline[0], outline[outline.length - 1])
  ) {
    return outline.slice(0, -1);
  }
  return outline.slice();
}

function closeOutline(outline: [number, number][]) {
  if (outline.length < 2) return outline.slice();
  const result = stripClosingPoint(outline);
  result.push([result[0][0], result[0][1]]);
  return result;
}

function contourLengthClosed(points: [number, number][]) {
  const pts = stripClosingPoint(points);
  if (pts.length < 2) return 0;
  let length = 0;
  for (let i = 0; i < pts.length; i += 1) {
    length += outlineDistance(pts[i], pts[(i + 1) % pts.length]);
  }
  return length;
}

function resampleClosedContour(
  points: [number, number][],
  stepPx: number,
): [number, number][] {
  const pts = stripClosingPoint(points);
  if (pts.length < 3) return closeOutline(pts);
  const step = Math.max(0.25, stepPx);
  const totalLength = contourLengthClosed(pts);
  if (!Number.isFinite(totalLength) || totalLength <= step) {
    return closeOutline(pts);
  }

  const sampleCount = Math.max(8, Math.ceil(totalLength / step));
  const result: [number, number][] = [];
  let edgeIndex = 0;
  let edgeStart = pts[0];
  let edgeEnd = pts[1 % pts.length];
  let edgeLength = outlineDistance(edgeStart, edgeEnd);
  let traversed = 0;

  for (let s = 0; s < sampleCount; s += 1) {
    const target = (s / sampleCount) * totalLength;
    while (traversed + edgeLength < target && edgeIndex < pts.length + 1) {
      traversed += edgeLength;
      edgeIndex += 1;
      edgeStart = pts[edgeIndex % pts.length];
      edgeEnd = pts[(edgeIndex + 1) % pts.length];
      edgeLength = outlineDistance(edgeStart, edgeEnd);
    }
    const t = edgeLength > 1e-6 ? (target - traversed) / edgeLength : 0;
    result.push([
      edgeStart[0] + (edgeEnd[0] - edgeStart[0]) * t,
      edgeStart[1] + (edgeEnd[1] - edgeStart[1]) * t,
    ]);
  }

  return closeOutline(result);
}

function smoothClosedContour(
  points: [number, number][],
  radius: number,
  iterations: number,
): [number, number][] {
  let pts = stripClosingPoint(points);
  if (pts.length < 4) return closeOutline(pts);
  const safeRadius = Math.max(0, radius);
  const safeIterations = Math.max(0, Math.floor(iterations));
  if (safeRadius <= 0 || safeIterations <= 0) return closeOutline(pts);

  const sigma = Math.max(0.35, safeRadius * 0.5);
  const windowRadius = Math.max(1, Math.ceil(safeRadius));
  const weights: number[] = [];
  let weightSum = 0;
  for (let i = -windowRadius; i <= windowRadius; i += 1) {
    const weight = Math.exp(-(i * i) / (2 * sigma * sigma));
    weights.push(weight);
    weightSum += weight;
  }

  for (let iter = 0; iter < safeIterations; iter += 1) {
    const next: [number, number][] = new Array(pts.length) as [
      number,
      number,
    ][];
    for (let i = 0; i < pts.length; i += 1) {
      let sx = 0;
      let sy = 0;
      for (let k = -windowRadius; k <= windowRadius; k += 1) {
        const idx = (i + k + pts.length) % pts.length;
        const weight = weights[k + windowRadius];
        sx += pts[idx][0] * weight;
        sy += pts[idx][1] * weight;
      }
      next[i] = [sx / weightSum, sy / weightSum];
    }
    pts = next;
  }

  return closeOutline(pts);
}

function perpendicularDistanceToLine(
  p: [number, number],
  a: [number, number],
  b: [number, number],
) {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-12) return outlineDistance(p, a);
  const t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2;
  return Math.hypot(p[0] - (a[0] + dx * t), p[1] - (a[1] + dy * t));
}

function rdpOpen(
  points: [number, number][],
  start: number,
  end: number,
  epsilon: number,
  keep: boolean[],
) {
  if (end <= start + 1) return;
  let maxDistance = -1;
  let index = -1;
  const a = points[start];
  const b = points[end];
  for (let i = start + 1; i < end; i += 1) {
    const distance = perpendicularDistanceToLine(points[i], a, b);
    if (distance > maxDistance) {
      maxDistance = distance;
      index = i;
    }
  }
  if (index >= 0 && maxDistance > epsilon) {
    keep[index] = true;
    rdpOpen(points, start, index, epsilon, keep);
    rdpOpen(points, index, end, epsilon, keep);
  }
}

function simplifyClosedContour(
  points: [number, number][],
  tolerancePx: number,
  minPoints: number,
  maxPoints: number,
): [number, number][] {
  const pts = stripClosingPoint(points);
  if (pts.length < 4) return closeOutline(pts);
  const minKeep = Math.max(4, Math.floor(minPoints));
  const maxKeep = Math.max(minKeep, Math.floor(maxPoints));
  if (pts.length <= minKeep) return closeOutline(pts);

  const ring = [...pts, pts[0]];
  let epsilon = Math.max(0.01, tolerancePx);
  let simplified = ring;
  for (let iter = 0; iter < 6; iter += 1) {
    const keep = new Array(ring.length).fill(false);
    keep[0] = true;
    keep[ring.length - 1] = true;
    rdpOpen(ring, 0, ring.length - 1, epsilon, keep);
    simplified = ring.filter((_, idx) => keep[idx]);
    if (simplified.length <= maxKeep || epsilon > tolerancePx * 8) break;
    epsilon *= 1.35;
  }

  if (simplified.length < minKeep) {
    const stride = Math.max(1, Math.floor(pts.length / minKeep));
    simplified = [];
    for (let i = 0; i < pts.length; i += stride) {
      simplified.push(pts[i]);
    }
    if (simplified.length > maxKeep) {
      const keepEvery = Math.ceil(simplified.length / maxKeep);
      simplified = simplified.filter((_, idx) => idx % keepEvery === 0);
    }
    simplified.push(simplified[0]);
  }

  return closeOutline(stripClosingPoint(simplified));
}

function catmullRomClosedContour(
  points: [number, number][],
  samplesPerSegment: number,
): [number, number][] {
  const pts = stripClosingPoint(points);
  if (pts.length < 3) return closeOutline(pts);
  const samples = Math.max(1, Math.floor(samplesPerSegment));
  const result: [number, number][] = [];

  for (let i = 0; i < pts.length; i += 1) {
    const p0 = pts[(i - 1 + pts.length) % pts.length];
    const p1 = pts[i];
    const p2 = pts[(i + 1) % pts.length];
    const p3 = pts[(i + 2) % pts.length];
    for (let j = 0; j < samples; j += 1) {
      const t = j / samples;
      const t2 = t * t;
      const t3 = t2 * t;
      result.push([
        0.5 *
          ((2 * p1[0]) +
            (-p0[0] + p2[0]) * t +
            (2 * p0[0] - 5 * p1[0] + 4 * p2[0] - p3[0]) * t2 +
            (-p0[0] + 3 * p1[0] - 3 * p2[0] + p3[0]) * t3),
        0.5 *
          ((2 * p1[1]) +
            (-p0[1] + p2[1]) * t +
            (2 * p0[1] - 5 * p1[1] + 4 * p2[1] - p3[1]) * t2 +
            (-p0[1] + 3 * p1[1] - 3 * p2[1] + p3[1]) * t3),
      ]);
    }
  }

  return closeOutline(result);
}

export function smoothClosedOutlinePoints(
  outline: [number, number][],
  options?: OutlineSmoothingOptions | false,
): [number, number][] {
  const opt = normalizeOutlineOptions(options);
  if (!opt.enabled || outline.length < 4) return outline;

  let processed = resampleClosedContour(outline, opt.resampleStepPx);
  processed = smoothClosedContour(
    processed,
    opt.smoothRadius,
    opt.smoothIterations,
  );
  processed = simplifyClosedContour(
    processed,
    opt.simplifyTolerancePx,
    opt.minPoints,
    opt.maxPoints,
  );
  return catmullRomClosedContour(processed, opt.splineSamplesPerSegment);
}

export function smoothOutlinePoints(
  outlines: [number, number][][],
  options?: OutlineSmoothingOptions | false,
): [number, number][][] {
  const opt = normalizeOutlineOptions(options);
  if (!opt.enabled) return outlines;
  return outlines
    .map((outline) => smoothClosedOutlinePoints(outline, opt))
    .filter((outline) => outline.length >= 2);
}

// create outline actor
// 아웃라인 데이터, 색상, 두께를 넣으면 actor 만들어주는 함수
// 보통 cross section vtk 처음 만들때 사용
// export function createOutlineActorFromPoints(
//   outlines: [number, number][][],
//   color: [number, number, number] = [1, 0, 0],
//   linewidth: number = 1,
// ) {
//   const points = vtkPoints.newInstance();
//   const lines = vtkCellArray.newInstance();

//   let pointIdx = 0;
//   outlines.forEach((outline) => {
//     const pointIds: number[] = [];
//     outline.forEach(([x, y]) => {
//       points.insertNextPoint(x, y, 0);
//       pointIds.push(pointIdx++);
//     });
//     if (pointIds.length >= 2) {
//       lines.insertNextCell(pointIds);
//     }
//   });

//   const polyData = vtkPolyData.newInstance();
//   polyData.setPoints(points);
//   polyData.setLines(lines);

//   const mapper = vtkMapper.newInstance();
//   mapper.setInputData(polyData);

//   const actor = vtkActor.newInstance();
//   actor.setMapper(mapper);
//   actor.getProperty().setColor(color);
//   actor.getProperty().setLineWidth(linewidth);

//   return actor;
// }

export function createOutlineActorFromPoints(
  outlines: [number, number][][],
  color: [number, number, number] = [1, 0, 0],
  fill: boolean = false,
  linewidth: number = 1,
  numInterp: number = 10, // 곡선 보간점 개수
) {
  const points = vtkPoints.newInstance();
  const lines = vtkCellArray.newInstance();
  // const polys = vtkCellArray.newInstance();

  // let pointIdx = 0;
  // outlines.forEach((outline) => {
  //   if (!outline || outline.length < 2) return;

  //   // Spline 보간
  //   const xs = outline.map(([x]) => x);
  //   const ys = outline.map(([, y]) => y);
  //   const size = xs.length;
  //   const tArray = Array.from({ length: size }, (_, i) => i);
  //   const work = new Float32Array(4 * size);
  //   const sx = vtkCardinalSpline1D.newInstance();
  //   const sy = vtkCardinalSpline1D.newInstance();
  //   sx.computeCloseCoefficients(size, work, tArray as Vector3, xs as Vector3);
  //   sy.computeCloseCoefficients(size, work, tArray as Vector3, ys as Vector3);

  //   // 곡선 보간점 만들기
  //   const smooth: [number, number][] = [];
  //   for (let i = 0; i < size - 1; i++) {
  //     for (let j = 0; j < numInterp; j++) {
  //       const t = j / numInterp;
  //       const x = sx.getValue(i, t);
  //       const y = sy.getValue(i, t);
  //       smooth.push([x, y]);
  //     }
  //   }
  //   // 마지막 점 추가
  //   smooth.push([xs[size - 1], ys[size - 1]]);

  //   // polyline 연결
  //   const pointIds: number[] = [];
  //   smooth.forEach(([x, y]) => {
  //     points.insertNextPoint(x, y, 0);
  //     pointIds.push(pointIdx++);
  //   });
  //   if (pointIds.length >= 2) {
  //     lines.insertNextCell(pointIds);
  //   }

  //   // if (fill) {
  //   //   // earcut은 flat array [x0, y0, x1, y1, ...] 필요
  //   //   const flat: number[] = [];
  //   //   smooth.forEach(([x, y]) => {
  //   //     flat.push(x, y);
  //   //   });
  //   //   // earcut은 flat좌표의 각 점 index 0,1,2...를 반환 (삼각형 인덱스)
  //   //   const indices = earcut(flat, null, 2);

  //   //   // VTK POLYS의 포맷: [3, i0, i1, i2, 3, i3, i4, i5, ...] (삼각형 당 4개)
  //   //   for (let i = 0; i < indices.length; i += 3) {
  //   //     const a = pointIds[indices[i]];
  //   //     const b = pointIds[indices[i + 1]];
  //   //     const c = pointIds[indices[i + 2]];
  //   //     polys.insertNextCell([a, b, c]);
  //   //   }
  //   // }
  // });

  // const polyData = vtkPolyData.newInstance();
  // polyData.setPoints(points);
  // if (fill) {
  //   polyData.setPolys(lines);
  //   //polyData.setPolys(polys);
  // } else {
  //   polyData.setLines(lines);
  // }

  let pointIdx = 0;
  outlines.forEach((outline) => {
    const pointIds: number[] = [];
    outline.forEach(([x, y]) => {
      points.insertNextPoint(x, y, 0);
      pointIds.push(pointIdx++);
    });
    if (pointIds.length >= 2) {
      lines.insertNextCell(pointIds);
    }
  });

  const polyData = vtkPolyData.newInstance();
  polyData.setPoints(points);
  polyData.setLines(lines);

  const mapper = vtkMapper.newInstance();
  mapper.setInputData(polyData);

  const actor = vtkActor.newInstance();
  actor.setMapper(mapper);
  actor.getProperty().setColor(color);
  actor.getProperty().setLineWidth(linewidth);

  return actor;
}

// create outline mapper
// 아웃라인에 데이터를 넣으면 mapper 만들어주는 함수
// 보통은 cross section index 조절하면서 갱신할 때 사용
export function createOutlineMapper(
  outlines: [number, number][][], // 여러 outline을 받음
  oldMapper?: vtkMapper | null,
  fill: boolean = false,
) {
  const polyData = oldMapper
    ? (oldMapper.getInputData() as vtkPolyData)
    : vtkPolyData.newInstance();
  const points = vtkPoints.newInstance();
  const lines = vtkCellArray.newInstance();
  const polys = vtkCellArray.newInstance();

  let pointIdx = 0;
  outlines.forEach((outline) => {
    const pointIds: number[] = [];
    outline.forEach(([x, y]) => {
      points.insertNextPoint(x, y, 0);
      pointIds.push(pointIdx++);
    });
    if (pointIds.length >= 2) {
      lines.insertNextCell(pointIds);
    }
  });

  // const numInterp = 10;

  // let pointIdx = 0;
  // outlines.forEach((outline) => {
  //   if (!outline || outline.length < 2) return;

  //   // Spline 보간
  //   const xs = outline.map(([x]) => x);
  //   const ys = outline.map(([, y]) => y);
  //   const size = xs.length;
  //   const tArray = Array.from({ length: size }, (_, i) => i);
  //   const work = new Float32Array(4 * size);
  //   const sx = vtkCardinalSpline1D.newInstance();
  //   const sy = vtkCardinalSpline1D.newInstance();
  //   sx.computeCloseCoefficients(size, work, tArray as Vector3, xs as Vector3);
  //   sy.computeCloseCoefficients(size, work, tArray as Vector3, ys as Vector3);

  //   // 곡선 보간점 만들기
  //   const smooth: [number, number][] = [];
  //   for (let i = 0; i < size - 1; i++) {
  //     for (let j = 0; j < numInterp; j++) {
  //       const t = j / numInterp;
  //       const x = sx.getValue(i, t);
  //       const y = sy.getValue(i, t);
  //       smooth.push([x, y]);
  //     }
  //   }
  //   // 마지막 점 추가
  //   smooth.push([xs[size - 1], ys[size - 1]]);

  //   // polyline 연결
  //   const pointIds: number[] = [];
  //   smooth.forEach(([x, y]) => {
  //     points.insertNextPoint(x, y, 0);
  //     pointIds.push(pointIdx++);
  //   });
  //   if (pointIds.length >= 2) {
  //     lines.insertNextCell(pointIds);
  //   }

  //   // if (fill) {
  //   //   // earcut은 flat array [x0, y0, x1, y1, ...] 필요
  //   //   const flat: number[] = [];
  //   //   smooth.forEach(([x, y]) => {
  //   //     flat.push(x, y);
  //   //   });
  //   //   // earcut은 flat좌표의 각 점 index 0,1,2...를 반환 (삼각형 인덱스)
  //   //   const indices = earcut(flat, null, 2);

  //   //   // VTK POLYS의 포맷: [3, i0, i1, i2, 3, i3, i4, i5, ...] (삼각형 당 4개)
  //   //   for (let i = 0; i < indices.length; i += 3) {
  //   //     const a = pointIds[indices[i]];
  //   //     const b = pointIds[indices[i + 1]];
  //   //     const c = pointIds[indices[i + 2]];
  //   //     polys.insertNextCell([a, b, c]);
  //   //   }
  //   // }
  // });

  // polyData.setPoints(points);
  // if (fill) {
  //   polyData.setPolys(lines);
  //   //polyData.setPolys(polys);
  // } else {
  //   polyData.setLines(lines);
  // }

  polyData.setPoints(points);
  polyData.setLines(lines);

  if (oldMapper) {
    oldMapper.setInputData(polyData);
    return oldMapper;
  }
  const mapper = vtkMapper.newInstance();
  mapper.setInputData(polyData);
  return mapper;
}

function makeVtkImageData(
  data: Int16Array | Uint8Array,
  width: number,
  height: number,
) {
  const image = vtkImageData.newInstance();
  image.setDimensions(width, height, 1);
  image.getPointData().setScalars(
    vtkDataArray.newInstance({
      name:
        data instanceof Int16Array
          ? 'hudata'
          : data instanceof Uint8Array
            ? 'Labels'
            : '',
      values: data,
      numberOfComponents: 1,
    }),
  );
  return image;
}

// ---------- 공통: 사전할당 핑퐁 헬퍼 ----------
type HUImg = { data: Int16Array; width: number; height: number };
type LblImg = { data: Uint8Array; width: number; height: number };

type UpscaleWorkBuffers = {
  huA?: Int16Array;
  huB?: Int16Array;
  labelA?: Uint8Array;
  labelB?: Uint8Array;
};

const rowdataUpscaleWorkBuffers = new WeakMap<object, UpscaleWorkBuffers>();

function ensureInt16(size: number, a?: Int16Array): Int16Array {
  return a && a.length >= size ? a : new Int16Array(size);
}
function ensureUint8(size: number, a?: Uint8Array): Uint8Array {
  return a && a.length >= size ? a : new Uint8Array(size);
}

// ---------- HU: bilinear의 2x를 정수 연산으로 빠르게 ----------
export function upscaleHU2xFast(
  src: Int16Array,
  width: number,
  height: number,
  dst?: Int16Array,
): HUImg {
  const dstW = width << 1;
  const dstH = height << 1;
  const out = ensureInt16(dstW * dstH, dst);

  const w1 = width - 1;
  const h1 = height - 1;

  // 내부 블록 (오른쪽/아래쪽 이웃이 안전한 영역)
  for (let y = 0; y < h1; ++y) {
    const srcRow = y * width;
    const srcRowB = (y + 1) * width;
    const dy = y << 1;
    const dstRow0 = dy * dstW;
    const dstRow1 = (dy + 1) * dstW;

    for (let x = 0; x < w1; ++x) {
      const i00 = srcRow + x;
      const i01 = i00 + 1;
      const i10 = srcRowB + x;
      const i11 = i10 + 1;

      const v00 = src[i00];
      const v01 = src[i01];
      const v10 = src[i10];
      const v11 = src[i11];

      // 정수 반올림 평균: (a+b+1)>>1, (a+b+c+d+2)>>2
      const TL = v00;
      const TR = (v00 + v01 + 1) >> 1;
      const BL = (v00 + v10 + 1) >> 1;
      const BR = (v00 + v01 + v10 + v11 + 2) >> 2;

      const dx = x << 1;
      out[dstRow0 + dx] = TL;
      out[dstRow0 + dx + 1] = TR;
      out[dstRow1 + dx] = BL;
      out[dstRow1 + dx + 1] = BR;
    }

    // 마지막 컬럼(x = w1) 경계(오른쪽 반복)
    {
      const x = w1;
      const i00 = srcRow + x;
      const i10 = srcRowB + x;
      const v00 = src[i00];
      const v10 = src[i10];

      const TL = v00;
      const TR = v00; // 오른쪽 이웃 = 자기 자신
      const BL = (v00 + v10 + 1) >> 1;
      const BR = BL; // (v00 + v10)/2

      const dx = x << 1;
      out[dstRow0 + dx] = TL;
      out[dstRow0 + dx + 1] = TR;
      out[dstRow1 + dx] = BL;
      out[dstRow1 + dx + 1] = BR;
    }
  }

  // 마지막 행(y = h1) 경계(아래 반복)
  {
    const y = h1;
    const srcRow = y * width;
    const dy = y << 1;
    const dstRow0 = dy * dstW;
    const dstRow1 = (dy + 1) * dstW;

    for (let x = 0; x < w1; ++x) {
      const i00 = srcRow + x;
      const i01 = i00 + 1;
      const v00 = src[i00];
      const v01 = src[i01];

      const TL = v00;
      const TR = (v00 + v01 + 1) >> 1;
      const BL = v00; // 아래 이웃 = 자기 자신
      const BR = TR; // (v00 + v01)/2

      const dx = x << 1;
      out[dstRow0 + dx] = TL;
      out[dstRow0 + dx + 1] = TR;
      out[dstRow1 + dx] = BL;
      out[dstRow1 + dx + 1] = BR;
    }

    // 모서리 (x=w1, y=h1)
    {
      const x = w1;
      const v00 = src[srcRow + x];

      const TL = v00;
      const TR = v00;
      const BL = v00;
      const BR = v00;

      const dx = x << 1;
      out[dstRow0 + dx] = TL;
      out[dstRow0 + dx + 1] = TR;
      out[dstRow1 + dx] = BL;
      out[dstRow1 + dx + 1] = BR;
    }
  }

  return { data: out, width: dstW, height: dstH };
}

// ---------- Label: Scale2x 계열 2x (함수 호출/경계분기 최소화) ----------
export function upscaleLabel2xFast(
  src: Uint8Array,
  width: number,
  height: number,
  dst?: Uint8Array,
): LblImg {
  const dstW = width << 1;
  const dstH = height << 1;
  const out = ensureUint8(dstW * dstH, dst);

  const w1 = width - 1;
  const h1 = height - 1;

  for (let y = 0; y < height; ++y) {
    const y0 = y > 0 ? y - 1 : 0;
    const y1 = y < h1 ? y + 1 : h1;
    const rowC = y * width;
    const rowA = y0 * width;
    const rowE = y1 * width;

    const dy = y << 1;
    const dstRow0 = dy * dstW;
    const dstRow1 = (dy + 1) * dstW;

    for (let x = 0; x < width; ++x) {
      const xL = x > 0 ? x - 1 : 0;
      const xR = x < w1 ? x + 1 : w1;

      const C = src[rowC + x];
      const B = src[rowC + xL];
      const D = src[rowC + xR];
      const A = src[rowA + x];
      const E = src[rowE + x];

      // Scale2x 규칙
      const cond = B !== D && A !== E;
      const TL = cond && B === A ? B : C;
      const TR = cond && D === A ? D : C;
      const BL = cond && B === E ? B : C;
      const BR = cond && D === E ? D : C;

      const dx = x << 1;
      out[dstRow0 + dx] = TL;
      out[dstRow0 + dx + 1] = TR;
      out[dstRow1 + dx] = BL;
      out[dstRow1 + dx + 1] = BR;
    }
  }

  return { data: out, width: dstW, height: dstH };
}

function dataUpscaleFunction(
  src: Uint8Array | Int16Array,
  width: number,
  height: number,
  n: number,
  isPaintData: boolean,
  workBuffers?: UpscaleWorkBuffers,
) {
  let img = { data: src, width, height };

  let curData = src;
  let curW = width;
  let curH = height;
  if (!isPaintData) {
    let tmpA = workBuffers?.huA;
    let tmpB = workBuffers?.huB;

    for (let i = 0; i < n; ++i) {
      const nextW = curW << 1;
      const nextH = curH << 1;
      const dst =
        (i & 1) === 0
          ? (tmpA = ensureInt16(nextW * nextH, tmpA))
          : (tmpB = ensureInt16(nextW * nextH, tmpB));
      if (workBuffers) {
        if ((i & 1) === 0) workBuffers.huA = tmpA;
        else workBuffers.huB = tmpB;
      }

      const out = upscaleHU2xFast(curData as Int16Array, curW, curH, dst);
      curData = out.data;
      curW = nextW;
      curH = nextH;
    }
  } else {
    let tmpA = workBuffers?.labelA;
    let tmpB = workBuffers?.labelB;

    for (let i = 0; i < n; ++i) {
      const nextW = curW << 1;
      const nextH = curH << 1;

      const dst =
        (i & 1) === 0
          ? (tmpA = ensureUint8(nextW * nextH, tmpA))
          : (tmpB = ensureUint8(nextW * nextH, tmpB));
      if (workBuffers) {
        if ((i & 1) === 0) workBuffers.labelA = tmpA;
        else workBuffers.labelB = tmpB;
      }

      const out = upscaleLabel2xFast(curData as Uint8Array, curW, curH, dst);
      curData = out.data;
      curW = nextW;
      curH = nextH;
    }
  }

  img.data = curData;
  img.width = curW;
  img.height = curH;

  return img;
}

export const rowdataUpscaleFunction = ({
  reslice,
  upscale = 0,
  isPaintData = false,
}: {
  reslice: vtkImageReslice;
  upscale: number;
  isPaintData?: boolean;
}) => {
  const reslicedImage = reslice.getOutputData();
  if (!reslicedImage) return null;
  const dims = reslicedImage.getDimensions();
  const scalars = reslicedImage.getPointData().getScalars();
  if (!scalars) return null;
  const labelArray = scalars.getData();
  const width = dims[0];
  const height = dims[1];
  let workBuffers = rowdataUpscaleWorkBuffers.get(reslice as unknown as object);
  if (!workBuffers) {
    workBuffers = {};
    rowdataUpscaleWorkBuffers.set(reslice as unknown as object, workBuffers);
  }
  const scaled = dataUpscaleFunction(
    labelArray,
    width,
    height,
    upscale,
    isPaintData,
    workBuffers,
  );
  const scaledImageData = makeVtkImageData(
    scaled.data,
    scaled.width,
    scaled.height,
  );
  return scaledImageData;
};

// create cross section vtk data
// vtk cross section 처음 actor, mapper 만들어주는 함수
export const createCrossSectionActorAndMapper = ({
  data,
  size,
  axes,
  currentVOI,
  isPaintData = false,
  colorPoints,
  opacityPoints,
  upscale,
}: {
  data: vtkImageData;
  size: number;
  axes: mat4;
  currentVOI: VOI;
  isPaintData?: boolean;
  colorPoints?: Array<[number, number, number, number]>;
  opacityPoints?: Array<[number, number]>;
  upscale?: number;
}) => {
  const reslice = vtkImageReslice.newInstance();
  reslice.setTransformInputSampling(false);
  reslice.setAutoCropOutput(false);
  reslice.setOutputDimensionality(2);
  reslice.setOutputScalarType('FLOAT');

  reslice.setOutputSpacing(data.getSpacing());
  const half = Math.floor(size * 0.5);
  const spatemp = data.getSpacing();
  const sizetemp = Math.floor(half * spatemp[0]);
  reslice.setOutputOrigin([-sizetemp, -sizetemp, 0]);
  reslice.setOutputExtent([0, half * 2 - 1, 0, half * 2 - 1, 0, 0]);
  reslice.setResliceAxes(axes);
  reslice.setInputData(data);

  reslice.setInterpolationMode(
    !isPaintData ? InterpolationMode.LINEAR : InterpolationMode.NEAREST,
  );

  const mapper = vtkImageMapper.newInstance();
  if (upscale) {
    const updata = rowdataUpscaleFunction({
      reslice: reslice,
      upscale: upscale,
      isPaintData: isPaintData,
    });
    if (updata) mapper.setInputData(updata);
    else mapper.setInputConnection(reslice.getOutputPort());
  } else {
    mapper.setInputConnection(reslice.getOutputPort());
  }
  mapper.setPreferSizeOverAccuracy(true);

  const actor =
    isPaintData && colorPoints && opacityPoints
      ? getColoredPaintActor(mapper, colorPoints, opacityPoints)
      : vtkImageSlice.newInstance();
  actor.setMapper(mapper);

  if (!isPaintData) {
    let windowWidth = currentVOI.windowWidth;
    let windowCenter = currentVOI.windowLevel;
    actor.getProperty().setColorLevel(windowCenter);
    actor.getProperty().setColorWindow(windowWidth);
  }

  return { mapper, actor, reslice };
};

// create cpr vtk data
// vtk cpr 처음 actor, mapper 만들어주는 함수
export const createCPRActorAndMapper = ({
  data,
  mode,
  width,
  centerline,
  currentVOI,
  isPaintData = false,
  colorPoints,
  opacityPoints,
}: {
  data: vtkImageData;
  mode: 'stretched' | 'straightened';
  width: number;
  centerline: vtkPolyData;
  currentVOI: VOI;
  isPaintData?: boolean;
  colorPoints?: Array<[number, number, number, number]>;
  opacityPoints?: Array<[number, number]>;
}) => {
  const mapper = vtkImageCPRMapper.newInstance();
  mapper.setOrientationArrayName('OrientationQuat');
  const actor =
    isPaintData && colorPoints && opacityPoints
      ? getColoredPaintActor(mapper, colorPoints, opacityPoints)
      : vtkImageSlice.newInstance();

  mapper.setImageData(data);
  mapper.setCenterlineData(centerline);

  const n = centerline.getPoints().getNumberOfPoints();
  const mid = Math.floor(n / 2);
  const centerPoint = centerline.getPoints().getPoint(mid) as [
    number,
    number,
    number,
  ];

  if (mode === 'straightened') {
    mapper.useStraightenedMode();
  } else {
    // 여기 center point를 바꾸면 회전 축이 바낌
    mapper.useStretchedMode(centerPoint);
  }
  mapper.setWidth(width);
  // mapper.setPreferSizeOverAccuracy(false);
  // @ts-ignore
  actor.setMapper(mapper);

  if (!isPaintData) {
    let windowWidth = currentVOI.windowWidth;
    let windowCenter = currentVOI.windowLevel;
    actor.getProperty().setColorLevel(windowCenter);
    actor.getProperty().setColorWindow(windowWidth);
    // actor.getProperty().setInterpolationTypeToNearest();
  }

  return { mapper, actor };
};

// render reset
export const resetRenderer = (
  renderWindow: vtkRenderWindow,
  renderer: vtkRenderer,
) => {
  const newRenderer = vtkRenderer.newInstance();
  renderer.removeAllActors();
  renderWindow.removeRenderer(renderer);
  renderWindow.addRenderer(newRenderer);
  renderWindow.getInteractor()?.setInteractorStyle(null);

  return newRenderer;
};

// calculate cpr angle view
// cpr 회전 값에 변경에 맞게 갱신해주는 함수
export const rotateView = ({
  radian,
  mapper,
  actor,
  renderer,
  savedTranslation,
  axis,
  mode,
}: {
  radian: number;
  mapper: vtkImageCPRMapper;
  actor: vtkImageSlice;
  renderer: vtkRenderer;
  savedTranslation: vec3;
  axis: vec3;
  mode: 'stretched' | 'straightened' | undefined;
}) => {
  const image = mapper.getInputData();
  if (!image) return;

  const imageSpacing = image.getSpacing();
  if (!imageSpacing) return;

  const origin = vec3.fromValues(0, 0, 0);
  const bitangentDir = vec3.fromValues(0, 1, 0);
  vec3.rotateZ(bitangentDir, bitangentDir, origin, radian);

  const width = mapper.getWidth();
  const center = mapper.getCenter() as vec3;
  let height = mapper.getHeight();

  const distanceStep = Math.min(...imageSpacing);
  const clampedDistance = Math.max(0, Math.min(height, distanceStep));

  const { orientation } =
    mapper.getCenterlinePositionAndOrientation(clampedDistance);
  if (!orientation || orientation.includes(NaN)) return;

  const modelDirections = mat3.fromQuat(mat3.create(), orientation);
  const inverseModelDirections = mat3.invert(mat3.create(), modelDirections);
  if (!inverseModelDirections) return;

  const worldNormal = vec3.create();
  const worldBitangent = vec3.create();
  const worldTangent = vec3.create();

  if (mode === 'straightened') {
    const normalDir = axis;
    vec3.transformMat3(worldBitangent, bitangentDir, modelDirections);
    vec3.transformMat3(worldNormal, normalDir, modelDirections);
    vec3.cross(worldTangent, worldBitangent, worldNormal);
  } else {
    vec3.copy(worldNormal, axis);
    if (vec3.sqrLen(worldNormal) < 1e-9) {
      vec3.set(worldNormal, 0, 0, 1);
    } else {
      vec3.normalize(worldNormal, worldNormal);
    }
    const upRef = vec3.fromValues(0, 0, 1);
    if (Math.abs(vec3.dot(worldNormal, upRef)) > 0.9) {
      vec3.set(upRef, 0, 1, 0);
    }

    const axisE0 = vec3.create();
    vec3.cross(axisE0, worldNormal, upRef);
    vec3.normalize(axisE0, axisE0);

    const axisE1 = vec3.create();
    vec3.cross(axisE1, worldNormal, axisE0);
    vec3.normalize(axisE1, axisE1);

    const tmp = vec3.create();
    vec3.scale(worldBitangent, axisE0, Math.cos(radian));
    vec3.scale(tmp, axisE1, Math.sin(radian));
    vec3.add(worldBitangent, worldBitangent, tmp);
    vec3.normalize(worldBitangent, worldBitangent);

    vec3.cross(worldTangent, worldBitangent, worldNormal);
  }

  vec3.normalize(worldBitangent, worldBitangent);
  vec3.normalize(worldNormal, worldNormal);
  vec3.normalize(worldTangent, worldTangent);

  const worldDirections = mat3.fromValues(
    worldTangent[0],
    worldTangent[1],
    worldTangent[2],
    worldBitangent[0],
    worldBitangent[1],
    worldBitangent[2],
    worldNormal[0],
    worldNormal[1],
    worldNormal[2],
  );
  const baseDirections = mat3.mul(
    mat3.create(),
    inverseModelDirections,
    worldDirections,
  );
  const inputData = (mapper as any).getInputData?.();
  if (!inputData || typeof inputData.getPointData !== 'function') {
    console.log('rotateView invalid input data for mapper', {
      inputDataType: typeof inputData,
      width,
      height,
      center,
      spacing: imageSpacing,
      clampedDistance,
      axis,
      mode,
    });
    return;
  }
  const centerlineData =
    (mapper as any).getCenterlineData?.() ||
    (mapper as any).getOrientedCenterline?.();
  const centerlinePoints =
    centerlineData?.getPoints?.()?.getNumberOfPoints?.() ?? null;
  const centerlineDataLen =
    centerlineData?.getPoints?.()?.getData?.()?.length ?? null;
  mapper.setDirectionMatrix(baseDirections);

  height = mapper.getHeight();
  const orientedCenterlinePost =
    (mapper as any).getOrientedCenterline?.() ??
    (mapper as any).getCenterlineData?.();
  const distancesPost = orientedCenterlinePost?.getDistancesToFirstPoint?.();
  const distancesPostHasNaN = Array.isArray(distancesPost)
    ? distancesPost.some((v) => Number.isNaN(v))
    : false;
  if (distancesPostHasNaN || !Number.isFinite(height)) {
    const orientationPost =
      mapper.getCenterlinePositionAndOrientation?.(clampedDistance)
        ?.orientation;
    const pointsData = orientedCenterlinePost?.getPoints?.()?.getData?.();
    const pointsHasNaN = Array.isArray(pointsData)
      ? pointsData.some((v) => Number.isNaN(v))
      : pointsData instanceof Float32Array || pointsData instanceof Float64Array
        ? Array.from(pointsData).some((v) => Number.isNaN(v))
        : 'unknown';
    console.log('rotateView invalid state', {
      height,
      distancesPostHasNaN,
      pointsHasNaN,
      orientationHasNaN: Array.isArray(orientationPost)
        ? orientationPost.some((v) => Number.isNaN(v))
        : 'unknown',
      width,
      center,
      spacing: imageSpacing,
      clampedDistance,
      centerlinePoints,
      centerlineDataLen,
      axis,
      mode,
    });
    const cached = lastValidCPRState.get(mapper);
    if (cached) {
      mapper.setDirectionMatrix(cached.direction);
      height = cached.height;
    }
    return;
  }
  lastValidCPRState.set(mapper, {
    direction: mat3.clone(baseDirections),
    height,
  });

  const worldActorTranslation = vec3.scaleAndAdd(
    vec3.create(),
    center,
    worldTangent,
    -0.5 * width,
  ) as vec3;
  vec3.scaleAndAdd(
    worldActorTranslation,
    worldActorTranslation,
    worldNormal,
    clampedDistance - height,
  );

  const worldActorTransform = mat4.fromValues(
    worldTangent[0],
    worldTangent[1],
    worldTangent[2],
    0,
    worldNormal[0],
    worldNormal[1],
    worldNormal[2],
    0,
    -worldBitangent[0],
    -worldBitangent[1],
    -worldBitangent[2],
    0,
    worldActorTranslation[0],
    worldActorTranslation[1],
    worldActorTranslation[2],
    1,
  );
  actor.setUserMatrix(worldActorTransform);

  const camera = renderer.getActiveCamera();
  const cameraFocalPoint = vec3.scaleAndAdd(
    vec3.create(),
    center,
    worldNormal,
    clampedDistance - 0.5 * height,
  ) as vec3;
  const cameraPosition = vec3.scaleAndAdd(
    vec3.create(),
    cameraFocalPoint,
    worldBitangent,
    camera.getDistance() * -1,
  ) as vec3;

  const viewUp = camera.getViewUp();
  const direction = camera.getDirectionOfProjection();
  const right = vec3.create();
  vec3.cross(right, direction, viewUp);
  vec3.normalize(right, right);

  const up = vec3.create();
  vec3.cross(up, right, direction);
  vec3.normalize(up, up);

  const [dx, dy] = savedTranslation;
  const translation = vec3.create();
  vec3.scaleAndAdd(translation, translation, right, -dx);
  vec3.scaleAndAdd(translation, translation, up, dy);
  if (mode === 'stretched') {
    const orientedCenterline =
      (mapper as any).getOrientedCenterline?.() ??
      (mapper as any).getCenterlineData?.();
    const distances = orientedCenterline?.getDistancesToFirstPoint?.();
    if (Array.isArray(distances) && distances.length > 0) {
      const centerDistance = distances[Math.floor(distances.length / 2)];
      if (Number.isFinite(centerDistance)) {
        const centerShift = 0.5 * height - centerDistance;
        vec3.scaleAndAdd(translation, translation, up, centerShift);
      }
    }
  }

  vec3.add(cameraPosition, cameraPosition, translation);
  vec3.add(cameraFocalPoint, cameraFocalPoint, translation);

  camera.setPosition(...(cameraPosition as [number, number, number]));
  camera.setFocalPoint(...(cameraFocalPoint as [number, number, number]));
  camera.setViewUp(...(worldNormal as [number, number, number]));
};

// control cpr angle view
// cpr 회전값 변경에 따른 갱신을 image와 label 둘다 컨트롤 하는 함수
export const handleCPRAngle = (
  context: React.MutableRefObject<CPRContext | null>,
  radAngle: number,
  savedTranslation: vec3,
  axis: vec3,
) => {
  if (!context.current) {
    console.log('not context current');
    return;
  }
  const { renderer, renderWindow, actorIds, mode } = context.current;

  if (!actorIds || !renderer) {
    console.log('not actor or renderer');
    return;
  }

  const CPRActor = actorIds['CPRActor'] as vtkImageSlice;
  const CPRPaintActor = actorIds['CPRPaintActor'] as vtkImageSlice | undefined;
  if (!CPRActor) {
    console.log('not actor');
    return;
  }
  const CPRMapper = CPRActor.getMapper() as unknown as vtkImageCPRMapper;
  const CPRPaintMapper = CPRPaintActor
    ? (CPRPaintActor.getMapper() as unknown as vtkImageCPRMapper)
    : null;
  if (!CPRMapper) {
    console.log('not mapper');
    return;
  }

  rotateView({
    radian: radAngle,
    actor: CPRActor as vtkImageSlice,
    mapper: CPRMapper as vtkImageCPRMapper,
    renderer,
    savedTranslation,
    axis,
    mode,
  });

  if (CPRPaintActor && CPRPaintMapper) {
    rotateView({
      radian: radAngle,
      actor: CPRPaintActor,
      mapper: CPRPaintMapper as vtkImageCPRMapper,
      renderer,
      savedTranslation,
      axis,
      mode,
    });
  }

  renderer.resetCameraClippingRange();
  renderWindow.render();
};

// calculate cpr center line path
// cpr center line 좌표 계산해주는 함수
export const pathFunc = (
  actor: vtkImageSlice,
  mapper: vtkImageCPRMapper,
  renderer: vtkRenderer,
  width: number,
  height: number,
  movey: number = 0,
) => {
  const centerline = mapper.getOrientedCenterline();
  const distances = centerline.getDistancesToFirstPoint();
  const orientations = centerline.getOrientations();
  const centerPoint = mapper.getCenterPoint();
  const pointsArr = centerline.getPoints().getData();
  const numPoints = pointsArr.length / 3;

  const computeLocalXDirection = (index: number) => {
    if (orientations && orientations[index]) {
      const tangentDirection = (
        mapper.getTangentDirection ? mapper.getTangentDirection() : [1, 0, 0]
      ) as vec3;
      const localX = vec3.create();
      return vec3.transformQuat(localX, tangentDirection, orientations[index]);
    }
    return [1, 0, 0];
  };

  const pts: [number, number, number][] = [];
  for (let i = 0; i < numPoints; i++) {
    const point = pointsArr.slice(i * 3, i * 3 + 3) as vec3;
    let x = mapper.getWidth() / 2;
    if (centerPoint) {
      const localX = computeLocalXDirection(i) as vec3;
      x += vec3.dot(vec3.subtract(vec3.create(), point, centerPoint), localX);
    }
    pts.push([x, distances[i], 0]);
  }

  const matrix = actor.getUserMatrix();
  const worldCoord = vec3.create();
  const finalPath: { x: number; y: number }[] = new Array(pts.length);
  const aspect = width / height;
  for (let i = 0; i < pts.length; i++) {
    vec3.transformMat4(worldCoord, pts[i], matrix);
    const renderPos = renderer.worldToNormalizedDisplay(
      worldCoord[0],
      worldCoord[1],
      worldCoord[2],
      aspect,
    );
    finalPath[i] = {
      x: renderPos[0] * width,
      y: renderPos[1] * height + movey,
    };
  }
  return finalPath;
};

// control opacity label actor
// cpr, cross section의 label opacity 조절하는 함수
export const LabelOpacityControl = (
  paintActor: vtkImageSlice,
  renderwindow: vtkRenderWindow,
  data: { index: number; opacity: number }[],
) => {
  if (!paintActor) {
    console.log('not paint actor');
    return;
  }
  if (!paintActor.getProperty()) {
    console.log('not paint actor Property');
    return;
  }
  const pwf = paintActor.getProperty().getPiecewiseFunction();
  if (pwf) {
    for (let i = 0; i < data.length; i++) {
      pwf.addPoint(data[i].index, data[i].opacity);
    }
    paintActor.getProperty().setPiecewiseFunction(0, pwf);
    // renderwindow.render();
  }
};

// calculate vtk cross section out line
// vtk cross section의 아웃라인 좌표 계산하는 함수
export function getvtkoutlinepoints(
  paint: vtkImageReslice,
  values: number[],
  onlyCenterRegion: boolean = false,
) {
  const [width, height] = paint.getOutputData().getDimensions();
  const imagedata = paint
    .getOutputData()
    .getPointData()
    .getScalars()
    .getData() as Uint8Array;
  const outlinePoints = traceOutlinesWithCenterOption(
    imagedata,
    width,
    height,
    values,
    onlyCenterRegion,
  );

  return outlinePoints;
}

export function getVtkImageOutlinePoints(
  data: vtkImageData,
  values: number[],
  onlyCenterRegion: boolean = false,
  smoothingOptions?: OutlineSmoothingOptions | false,
) {
  const dims = data.getDimensions();
  const width = dims[0],
    height = dims[1];
  const labelArr = data.getPointData().getScalars().getData() as Uint8Array;
  const rawOutlinePoints = traceOutlinesWithCenterOption(
    labelArr,
    width,
    height,
    values,
    onlyCenterRegion,
  );
  const outlinePoints = smoothingOptions
    ? smoothOutlinePoints(rawOutlinePoints, smoothingOptions)
    : rawOutlinePoints;
  const origin = data.getOrigin() as [number, number, number];
  const spacing = data.getSpacing() as [number, number, number];
  const ox = origin[0] ?? 0;
  const oy = origin[1] ?? 0;
  const sx = spacing[0] ?? 1;
  const sy = spacing[1] ?? 1;

  return outlinePoints.map((outline) =>
    outline.map(([x, y]) => [ox + x * sx, oy + y * sy] as [number, number]),
  );
}

function makeLabelLookupTable(values: number[]): Uint8Array {
  const table = new Uint8Array(256);
  for (const value of values) {
    const label = value | 0;
    if (label >= 0 && label <= 255) table[label] = 1;
  }
  return table;
}

function hasLabelAt(
  data: ArrayLike<number>,
  table: Uint8Array,
  index: number,
): boolean {
  const value = data[index] | 0;
  return value >= 0 && value <= 255 && table[value] === 1;
}

function transformOutlineSegmentPoint(
  x: number,
  y: number,
  origin: [number, number, number],
  spacing: [number, number, number],
): [number, number] {
  return [origin[0] + x * spacing[0], origin[1] + y * spacing[1]];
}

export function getFastVtkImageBoundaryOutlinePoints(
  data: vtkImageData,
  values: number[],
): [number, number][][] {
  const dims = data.getDimensions();
  const width = dims[0] ?? 0;
  const height = dims[1] ?? 0;
  if (width < 2 || height < 2 || values.length === 0) return [];

  const scalars = data.getPointData().getScalars();
  const labelArr = scalars?.getData?.() as ArrayLike<number> | undefined;
  if (!labelArr) return [];

  const labelTable = makeLabelLookupTable(values);
  const origin = data.getOrigin() as [number, number, number];
  const spacing = data.getSpacing() as [number, number, number];
  const outlines: [number, number][][] = [];

  const pushSegment = (
    ax: number,
    ay: number,
    bx: number,
    by: number,
  ) => {
    outlines.push([
      transformOutlineSegmentPoint(ax, ay, origin, spacing),
      transformOutlineSegmentPoint(bx, by, origin, spacing),
    ]);
  };

  for (let y = 0; y < height - 1; y += 1) {
    const row = y * width;
    const nextRow = row + width;
    for (let x = 0; x < width - 1; x += 1) {
      const tl = hasLabelAt(labelArr, labelTable, row + x);
      const tr = hasLabelAt(labelArr, labelTable, row + x + 1);
      const br = hasLabelAt(labelArr, labelTable, nextRow + x + 1);
      const bl = hasLabelAt(labelArr, labelTable, nextRow + x);
      const mask = (tl ? 1 : 0) | (tr ? 2 : 0) | (br ? 4 : 0) | (bl ? 8 : 0);

      switch (mask) {
        case 0:
        case 15:
          break;
        case 1:
        case 14:
          pushSegment(x, y + 0.5, x + 0.5, y);
          break;
        case 2:
        case 13:
          pushSegment(x + 0.5, y, x + 1, y + 0.5);
          break;
        case 3:
        case 12:
          pushSegment(x, y + 0.5, x + 1, y + 0.5);
          break;
        case 4:
        case 11:
          pushSegment(x + 1, y + 0.5, x + 0.5, y + 1);
          break;
        case 5:
          pushSegment(x + 0.5, y, x + 1, y + 0.5);
          pushSegment(x, y + 0.5, x + 0.5, y + 1);
          break;
        case 6:
        case 9:
          pushSegment(x + 0.5, y, x + 0.5, y + 1);
          break;
        case 7:
        case 8:
          pushSegment(x, y + 0.5, x + 0.5, y + 1);
          break;
        case 10:
          pushSegment(x, y + 0.5, x + 0.5, y);
          pushSegment(x + 1, y + 0.5, x + 0.5, y + 1);
          break;
      }
    }
  }

  return outlines;
}

export function replaceVoxelValuesCopy(
  originalImageData: vtkImageData,
  from: number[],
  to: number[],
): vtkImageData {
  if (from.length !== to.length) {
    throw new Error('from과 to의 배열 길이는 같아야 합니다!');
  }

  // 1. geometry 복사
  const newImageData = vtkImageData.newInstance();
  newImageData.setDimensions(originalImageData.getDimensions());
  const spac = originalImageData.getSpacing() as number[];
  newImageData.setSpacing(spac);
  newImageData.setOrigin(originalImageData.getOrigin());
  newImageData.setDirection(originalImageData.getDirection());

  // 2. 데이터 복사
  const origScalars = originalImageData.getPointData().getScalars();
  const origData = origScalars.getData();
  const newData = new Uint8Array(origData.length);

  // 3. from → to 매핑 Map 생성 (성능 개선)
  const mapping = new Map<number, number>();
  for (let i = 0; i < from.length; ++i) {
    mapping.set(from[i], to[i]);
  }

  for (let i = 0; i < origData.length; ++i) {
    // 매핑 테이블에 있으면 변환, 아니면 그대로
    newData[i] = mapping.has(origData[i])
      ? mapping.get(origData[i])!
      : origData[i];
  }

  // 4. Scalars 생성 및 할당
  const newScalars = vtkDataArray.newInstance({
    name: origScalars.getName() || 'Scalars',
    values: newData,
    numberOfComponents: origScalars.getNumberOfComponents(),
  });
  newImageData.getPointData().setScalars(newScalars);

  return newImageData;
}

export function colorandopacitycontrol2D(
  analysisType: string,
  target: any,
  newSegmentation: any,
  actor: vtkImageSlice,
) {
  const clr = newSegmentation[target].color;
  const opa =
    (newSegmentation[target].visibility ? newSegmentation[target].opacity : 0) *
    0.5;

  let colorPoints: Array<[number, number, number, number]> = [];
  let opacityPoints: Array<[number, number]> = [];
  let colors_temp = { ...configStore.get().colors };
  if (target === 'aorta') {
    const avcopa =
      (newSegmentation['avc'].visibility ? newSegmentation['avc'].opacity : 0) *
      0.5;
    colorPoints = updateAVCColor({
      aortacolor: clr,
    });
    opacityPoints = updateAVCOpacity({
      aortaopacity: opa,
      avcopacity: avcopa,
      aortacolor: newSegmentation['aorta'].color,
    });
    if (analysisType !== 'aorticValveCalcium') return true;
    let colorarr = [];
    for (const clr of colorPoints) {
      colorarr.push(rgbToNormalizedARGB(clr));
    }
    colorPoints = colorarr;
  } else if (target === 'avc') {
    const aoopa =
      (newSegmentation['aorta'].visibility
        ? newSegmentation['aorta'].opacity
        : 0) * 0.5;
    colorPoints = updateAVCColor({
      avccolor: clr,
    });
    opacityPoints = updateAVCOpacity({
      aortaopacity: aoopa,
      avcopacity: opa,
      aortacolor: newSegmentation['aorta'].color,
    });
    if (analysisType !== 'aorticValveCalcium') return true;
    let colorarr = [];
    for (const clr of colorPoints) {
      colorarr.push(rgbToNormalizedARGB(clr));
    }
    colorPoints = colorarr;
  } else if (target === 'coronary') {
    colors_temp.LUMEN = rgbArrayToHex(clr);
    configStore.setData({ colors: colors_temp });
    colorPoints = makeCoronaryColorPoints(colors_temp);
    opacityPoints = makeOpacityPoints(analysisType, [
      target,
      opa,
      'segmanager',
    ]);
    configStore.get().label.lumen = opa > 0 ? true : false;
  } else if (target === 'calcium') {
    colors_temp.CALCIUM = rgbArrayToHex(clr);
    configStore.setData({ colors: colors_temp });
    colorPoints = makeCoronaryColorPoints(colors_temp);
    opacityPoints = makeOpacityPoints(analysisType, [
      target,
      opa,
      'segmanager',
    ]);
  } else {
    return false;
  }

  let lut = actor.getProperty().getRGBTransferFunction();
  let pwf = actor.getProperty().getPiecewiseFunction();
  if (!lut || !pwf) return false;
  colorPoints.forEach(([x, r, g, b]) => {
    lut.addRGBPoint(x, r, g, b);
  });
  lut.updateRange();
  opacityPoints.forEach(([x, opacity]) => {
    pwf.addPoint(x, opacity);
  });

  actor.getProperty().setPiecewiseFunction(0, pwf);
  actor.getProperty().setRGBTransferFunction(0, lut);
  return true;
}
